import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StatusSelect from '../../../Components/StatusSelect';
import useSavedTraders from '../useSavedTraders';
import { sizeLabel } from '../companySize';
import CustomsTags from './CustomsTags';
import { useCustomsEnabled } from '../customsTreatments';
import './SizeFilter.css';
import Spinner from '../../../Components/Spinner';
import './TradersTable.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/* What to sort by, and a separate direction toggle. The stored value is
 * still "<field>-<asc|desc>" (e.g. import-customs-desc), so a saved choice
 * carries over. Picking a new field starts at its natural direction. */
const SORT_FIELDS = [
    { key: 'import-customs', label: 'Import entries', natural: 'desc' },
    { key: 'imports', label: 'Import months', natural: 'desc' },
    { key: 'export-customs', label: 'Export entries', natural: 'desc' },
    { key: 'exports', label: 'Export months', natural: 'desc' },
    { key: 'customs', label: 'Border treatments', natural: 'desc' },
    { key: 'name', label: 'Company name', natural: 'asc' },
];

const splitSort = (sortBy) => {
    const i = String(sortBy || '').lastIndexOf('-');
    const field = i > 0 ? sortBy.slice(0, i) : 'import-customs';
    const dir = sortBy?.slice(i + 1) === 'asc' ? 'asc' : 'desc';
    return SORT_FIELDS.some((f) => f.key === field) ? { field, dir } : { field: 'import-customs', dir: 'desc' };
};

const dirLabel = (field, dir) =>
    field === 'name'
        ? (dir === 'asc' ? 'A → Z' : 'Z → A')
        : (dir === 'desc' ? 'High → low' : 'Low → high');

const fmtAddress = (t) =>
    [t.address_1, t.address_2, t.address_3, t.address_4, t.address_5, t.post_code]
        .filter(Boolean)
        .join(', ') || 'No address';

const sortTraders = (list, sortBy) => {
    if (!list || list.length === 0) return list;
    const lastHyphen = sortBy.lastIndexOf('-');
    const field = sortBy.substring(0, lastHyphen);
    const dir = sortBy.substring(lastHyphen + 1) === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => {
        // Hidden (free-search) rows have no name: they go after the named ones.
        if (field === 'name' && Boolean(a.locked) !== Boolean(b.locked)) return a.locked ? 1 : -1;
        let cmp = 0;
        switch (field) {
            case 'name':
                cmp = (a.company_name || '').localeCompare(b.company_name || '');
                break;
            case 'imports':
                cmp = (a.import_count || 0) - (b.import_count || 0);
                break;
            case 'customs':
                cmp = (a.customs_treatments || []).length - (b.customs_treatments || []).length;
                break;
            case 'exports':
                cmp = (a.export_count || 0) - (b.export_count || 0);
                break;
            case 'import-customs':
                cmp = (a.import_peak_activity || 0) - (b.import_peak_activity || 0);
                break;
            case 'export-customs':
                cmp = (a.export_peak_activity || 0) - (b.export_peak_activity || 0);
                break;
            default:
                cmp = 0;
        }
        return cmp * dir;
    });
};

const TradersTable = ({
    traders,
    allCount,
    lockedCount = 0,
    // How many companies the search showed in full (before refine filters).
    openCount = 0,
    loading,
    sortBy,
    onSortChange,
    hasSearched,
    onResetPaginationRef,
    emptyContent = null,
    // Optional extra line under a company's name (e.g. "Started importing June 2026").
    renderTag = null,
}) => {
    const customsOn = useCustomsEnabled(); // org setting — no "Might need" column when off
    const sortFields = customsOn ? SORT_FIELDS : SORT_FIELDS.filter((f) => f.key !== 'customs');
    const navigate = useNavigate();
    const [savedTraders, handleStatusChange] = useSavedTraders(traders);
    const [page, setPage] = useState(() => {
        const v = localStorage.getItem('traders_current_page');
        return v ? Number(v) : 1;
    });
    const [pageSize, setPageSize] = useState(() => {
        const v = localStorage.getItem('traders_page_size');
        return v ? Number(v) : 25;
    });

    useEffect(() => {
        if (!onResetPaginationRef) return;
        onResetPaginationRef({
            current: () => {
                setPage(1);
                localStorage.setItem('traders_current_page', '1');
            },
        });
    }, [onResetPaginationRef]);

    const chosenSort = splitSort(sortBy);
    // A saved "Border treatments" sort means nothing once the org turns customs off.
    const sort = !customsOn && chosenSort.field === 'customs' ? { field: 'import-customs', dir: 'desc' } : chosenSort;
    const sorted = useMemo(
        () => sortTraders(traders, `${sort.field}-${sort.dir}`),
        [traders, sort.field, sort.dir],
    );
    const total = sorted.length;
    const pageStart = (page - 1) * pageSize;
    const pageRows = useMemo(
        () => sorted.slice(pageStart, pageStart + pageSize),
        [sorted, pageStart, pageSize],
    );
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const goUpgrade = () => navigate('/subscription');

    useEffect(() => {
        if (page > pageCount) setPage(1);
    }, [pageCount, page]);

    return (
        /* No heading of its own: the page's header row already shows the
         * count, the same in list and map view. Just the sort, on the right. */
        <section className="section traders-results">
            <div className="section-head">
                <span />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label className="sort-label" htmlFor="traders-sort">Sort by</label>
                    <div className="form-field" style={{ marginBottom: 0 }}>
                        <select
                            id="traders-sort"
                            value={sort.field}
                            onChange={(e) => {
                                const next = sortFields.find((f) => f.key === e.target.value);
                                onSortChange(`${next.key}-${next.natural}`);
                            }}
                            style={{ width: 170, padding: '6px 10px', fontSize: 12 }}
                        >
                            {sortFields.map((f) => (
                                <option key={f.key} value={f.key}>
                                    {f.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        className="sort-dir"
                        onClick={() => onSortChange(`${sort.field}-${sort.dir === 'asc' ? 'desc' : 'asc'}`)}
                        aria-label={`Sort direction: ${dirLabel(sort.field, sort.dir)}. Click to reverse.`}
                        title="Reverse the order"
                    >
                        {dirLabel(sort.field, sort.dir)}
                    </button>
                </div>
            </div>

            {loading ? (
                <Spinner block label="Searching" />
            ) : total === 0 ? (
                <div className="empty">
                    {hasSearched && emptyContent ? (
                        // The page brings its own copy: suggestions and fixes.
                        emptyContent
                    ) : (
                        <>
                            <span className="kicker">
                                {hasSearched ? 'No matches' : 'No results yet'}
                            </span>
                            <p>
                                {hasSearched
                                    ? 'Try widening the search or relaxing the refine filters.'
                                    : 'Enter a company name, postcode, place or region on the left — or just pick a business type, size or commodity — then press Search.'}
                            </p>
                        </>
                    )}
                </div>
            ) : (
                <>
                    <div className="recent-table-wrap">
                        <table className="fs-table">
                            <thead>
                                <tr>
                                    <th>Company</th>
                                    <th style={{ width: 160 }}>Imports</th>
                                    <th style={{ width: 160 }}>Exports</th>
                                    {customsOn && <th style={{ width: 200 }}>Might need</th>}
                                    <th style={{ textAlign: 'right', width: 160 }}>
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((t, rowIdx) => {
                                    if (t.locked) {
                                        /* Past the free-search limit: the real figures,
                                         * size and area, but not who it is. */
                                        return (
                                            <tr key={t.lock_id} className="masked-row clickable" onClick={goUpgrade}>
                                                <td className="co">
                                                    <span className="masked-name" aria-label="Company name hidden">Company hidden</span>
                                                    {sizeLabel(t.company) && (
                                                        <span className="size-chip">{sizeLabel(t.company)}</span>
                                                    )}
                                                    <small>{t.outcode ? `${t.outcode} area` : 'Area hidden'}</small>
                                                </td>
                                                <td className="num">
                                                    {t.import_peak_activity || 0}+ entries
                                                    <span className="stack-2">{t.import_count || 0} months</span>
                                                </td>
                                                <td className="num">
                                                    {t.export_peak_activity || 0}+ entries
                                                    <span className="stack-2">{t.export_count || 0} months</span>
                                                </td>
                                                {customsOn && <td><CustomsTags keys={t.customs_treatments} /></td>}
                                                <td style={{ textAlign: 'right' }}>
                                                    <button type="button" className="masked-cta" onClick={(e) => { e.stopPropagation(); goUpgrade(); }}>
                                                        Subscribe to see
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    }
                                    const saved = savedTraders.get(t.trader_id);
                                    const status = saved ? saved.status : 'Not Saved';
                                    return (
                                        <tr
                                            key={t.trader_id}
                                            className="clickable"
                                            data-onboard={rowIdx === 0 ? 'trader-row' : undefined}
                                            onClick={() =>
                                                navigate(`/traders/${t.trader_id}`)
                                            }
                                        >
                                            <td className="co">
                                                {t.company_name || 'Unknown company'}
                                                {sizeLabel(t.company) && (
                                                    <span className="size-chip" title="Company size, from Companies House accounts">
                                                        {sizeLabel(t.company)}
                                                    </span>
                                                )}
                                                {renderTag ? renderTag(t) : null}
                                                <small>{fmtAddress(t)}</small>
                                            </td>
                                            <td className="num">
                                                {t.import_peak_activity || 0}+ entries
                                                <span className="stack-2">
                                                    {t.import_count || 0} months
                                                </span>
                                            </td>
                                            <td className="num">
                                                {t.export_peak_activity || 0}+ entries
                                                <span className="stack-2">
                                                    {t.export_count || 0} months
                                                </span>
                                            </td>
                                            {customsOn && <td><CustomsTags keys={t.customs_treatments} /></td>}
                                            <td
                                                style={{ textAlign: 'right' }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <StatusSelect
                                                    traderId={t.trader_id}
                                                    value={status}
                                                    onChange={handleStatusChange}
                                                    placement="left"
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {lockedCount > 0 && (
                        <div className="locked-strip">
                            <span>
                                Your free searches show {openCount.toLocaleString()} of{' '}
                                {(openCount + lockedCount).toLocaleString()} companies in full.
                            </span>
                            <button type="button" onClick={goUpgrade}>
                                Subscribe to see all results →
                            </button>
                        </div>
                    )}

                    <div className="pager">
                        <span className="pager-info">
                            {pageStart + 1}–{Math.min(pageStart + pageSize, total)} of {total}
                        </span>
                        <div className="pager-controls">
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    const ps = Number(e.target.value);
                                    setPageSize(ps);
                                    setPage(1);
                                    localStorage.setItem(
                                        'traders_page_size',
                                        String(ps),
                                    );
                                    localStorage.setItem('traders_current_page', '1');
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map((n) => (
                                    <option key={n} value={n}>
                                        {n} per page
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={page <= 1}
                                onClick={() => {
                                    const np = Math.max(1, page - 1);
                                    setPage(np);
                                    localStorage.setItem(
                                        'traders_current_page',
                                        String(np),
                                    );
                                }}
                            >
                                ← Prev
                            </button>
                            <span className="pager-page">
                                {page} / {pageCount}
                            </span>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={page >= pageCount}
                                onClick={() => {
                                    const np = Math.min(pageCount, page + 1);
                                    setPage(np);
                                    localStorage.setItem(
                                        'traders_current_page',
                                        String(np),
                                    );
                                }}
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export default TradersTable;
